import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Contrat du rig partagé : interdit. La lumière est calculée dans le
// raymarching (lampe de la caméra, contre-jour, lueur des arêtes).
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'fractal',
  family: 'shader',
  danceability: 0.80,
  requiresTier: 1,
  energyLevel: 0.78,
  bpmMin: 110,
  bpmMax: 180,
  movement: 0.85,
  tonality: 0.55,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// Fractal : un vol à travers une fractale raymarchée dont les paramètres de
// repliement pulsent sur le temps. Psytrance, techno, trance, IDM.
//
// EXCEPTION AU CONTRAT : raymarching plein écran à demi-résolution (tiers au
// tier 1) dans une cible privée, remontée par le quad d'affichage. Avec
// Nebula, le plus coûteux de la bibliothèque.
//
// QUATRE FRACTALES, formules standard (une DE fausse donne des trous ou une
// caméra qui traverse les murs) :
//   MENGER      l'éponge, 5 niveaux, PAVÉE (période 2) : des couloirs
//               orthogonaux libres par construction le long des axes.
//   KIFS        IFS kaléidoscopique de Knighty, 10 itérations, pavé (période 6).
//   MANDELBOX   repliement de boîte + de sphère, échelle -1,8 à -2,2.
//   APOLLONIAN  inversions sphériques (la formule d'iq), périodique par fract.
// Un MATÉRIAU PAR FRACTALE ET PAR TIER, en cache au niveau du module et
// jamais disposé (voir [[pi-shader-compile-cost]]) : quatre petits programmes
// compilent plus vite qu'un seul programme à branches, et ne sont compilés
// qu'une fois par page.
//
// LE DÉTAIL DÉCISIF : les paramètres de repliement reçoivent une impulsion
// par temps (1 à 3 %, montée 40 ms, retour 300 ms). La fractale est
// auto-similaire : une petite variation reconfigure toutes les échelles à la
// fois, les murs proches se décalent, les lointains se réorganisent. Au-delà
// de 3 %, la structure explose.
//
// LA CAMÉRA NE TRAVERSE JAMAIS LA MATIÈRE, par construction : la même DE est
// portée en JavaScript, et l'avance par frame est bornée à la moitié de la
// DE à la position courante - la propriété de Lipschitz qui fait marcher le
// sphere tracing fait aussi marcher la caméra. Autour de ça, l'évitement :
// la DE est sondée dans cinq directions autour de l'axe et la caméra
// s'oriente doucement vers l'espace le plus libre ; loin de tout (DE grande)
// elle redescend le gradient de la DE vers la structure. Pour MENGER elle
// suit simplement l'axe d'un couloir.
//
// precision highp obligatoire : une DE en mediump fait du bruit et des trous.

const KINDS = ['menger', 'kifs', 'mandelbox', 'apollonian'];
const TIER = {
  // tier 0 : refuge si le gouverneur descend en cours de composition
  0: { scale: 0.25, steps: 32, kinds: ['menger', 'kifs'], glow: false, normals: false, eps: 0.002 },
  1: { scale: 1 / 3, steps: 44, kinds: ['menger', 'kifs'], glow: false, normals: false, eps: 0.002 },
  2: { scale: 0.5, steps: 72, kinds: KINDS, glow: true, normals: true, eps: 0.0012 }
};
const ITER = { menger: 5, kifs: 10, mandelbox: 10, apollonian: 8 };
// Le Pi (1280 x 400, chaîne complète) : Menger 29 ms au tier 1, KIFS 85 ms à
// 10 itérations et 44 pas. Le coût est pas x itérations ; en deçà du tier 2
// le KIFS passe à 5 itérations et 30 pas (mesuré ensuite, voir la note).
const TIER_KIFS = { 0: { iter: 4, steps: 22 }, 1: { iter: 4, steps: 26 } };

// --- les paramètres de base, et la cellule traversée par mesure ----------------------
const BASE = {
  menger: { scale: 3.0, fold: 1.0, minR2: 0.25, fixR2: 1.0, offset: [1, 1, 1], cell: 2.0 },
  kifs: { scale: 1.5, fold: 1.0, minR2: 0.25, fixR2: 1.0, offset: [1.0, 0.9, 0.6], cell: 2.0, angle: 0.4 },
  mandelbox: { scale: -2.0, fold: 1.0, minR2: 0.25, fixR2: 1.0, offset: [1, 1, 1], cell: 3.0 },
  apollonian: { scale: 1.2, fold: 1.0, minR2: 0.25, fixR2: 1.0, offset: [1, 1, 1], cell: 2.0 }
};
const PULSE_MIN = 0.012, PULSE_MAX = 0.03;    // 1 à 3 % du paramètre, par bands.low
const PULSE_ATTACK_S = 0.04, PULSE_DECAY_S = 0.1;   // retour en 300 ms (3 tau)
const BAR_ANGLE_STEP = 0.07;                  // le cran de rotation interne, par mesure
const PHRASE_SCALE = 0.06;                    // la variation d'échelle sur une phrase
const PHRASE_LERP_BEATS = 2;
const MAJOR_EVERY_PHRASES = 4;                // sans analyse : une phrase majeure toutes les 4
const FLASH_S = 0.28;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const ROLL_BEATS = 64;
const FOV_MIN = 70, FOV_MAX = 76;
const PROBE_DIST = 0.35, PROBE_TILT = 0.38;   // les sondes d'évitement : distance, inclinaison (rad)
const STEER_TAU_S = 0.6;
const FREE_DE = 0.7;                          // au-delà, « loin de tout » : retour vers la structure
const TARGET_DE = 0.22;                       // la distance au mur que la caméra recherche
const MIN_DE = 0.035;                         // en deçà, la caméra rampe
const SAFE_FLOOR = 0.45;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================
// LES DE, EN JAVASCRIPT - la même formule que le shader, pour la caméra.
// p : [x,y,z] ; prm : { scale, fold, minR2, fixR2, offset, angle }
// =============================================================================
function rot2(x, y, a) { const c = Math.cos(a), s = Math.sin(a); return [c * x - s * y, s * x + c * y]; }
function modc(x, m) { return x - Math.floor(x / m) * m; }

export function deMenger(p, prm) {
  // le pavage, puis la rotation de l'espace autour de l'axe du couloir (x)
  let qx = modc(p[0] + 1, 2) - 1, qy = modc(p[1] + 1, 2) - 1, qz = modc(p[2] + 1, 2) - 1;
  [qy, qz] = rot2(qy, qz, prm.angle);
  let d = -1e5, s = 1;
  for (let m = 0; m < ITER.menger; m++) {
    const ax = modc(qx * s, 2) - 1, ay = modc(qy * s, 2) - 1, az = modc(qz * s, 2) - 1;
    s *= prm.scale;
    const rx = Math.abs(prm.fold - prm.scale * Math.abs(ax)), ry = Math.abs(prm.fold - prm.scale * Math.abs(ay)), rz = Math.abs(prm.fold - prm.scale * Math.abs(az));
    const da = Math.max(rx, ry), db = Math.max(ry, rz), dc = Math.max(rz, rx);
    const c = (Math.min(da, Math.min(db, dc)) - 1) / s;
    d = Math.max(d, c);
  }
  return d;
}

export function deKifs(p, prm) {
  let x = modc(p[0] + 2, 4) - 2, y = modc(p[1] + 2, 4) - 2, z = modc(p[2] + 2, 4) - 2;
  const sc = prm.scale, o = prm.offset, n = prm.iter || ITER.kifs;
  for (let i = 0; i < n; i++) {
    x = Math.abs(x); y = Math.abs(y); z = Math.abs(z);
    if (x < y) { const t = x; x = y; y = t; }
    if (x < z) { const t = x; x = z; z = t; }
    if (y < z) { const t = y; y = z; z = t; }
    [x, y] = rot2(x, y, prm.angle);
    x = sc * x - o[0] * (sc - 1); y = sc * y - o[1] * (sc - 1); z = sc * z - o[2] * (sc - 1);
  }
  // la DE du KIFS est une approximation : prudente côté caméra
  return (Math.sqrt(x * x + y * y + z * z) - 2) * Math.pow(sc, -n) * 0.6;
}

export function deMandelbox(p0, prm) {
  // une rotation de l'espace entier : par itération, elle déchiquette la boîte
  const p = [p0[0], ...rot2(p0[1], p0[2], prm.angle)];
  let x = p[0], y = p[1], z = p[2], dr = 1;
  const sc = prm.scale, fl = prm.fold, mr2 = prm.minR2, fr2 = prm.fixR2;
  for (let i = 0; i < ITER.mandelbox; i++) {
    x = Math.max(-fl, Math.min(fl, x)) * 2 - x; y = Math.max(-fl, Math.min(fl, y)) * 2 - y; z = Math.max(-fl, Math.min(fl, z)) * 2 - z;
    const r2 = x * x + y * y + z * z;
    if (r2 < mr2) { const k = fr2 / mr2; x *= k; y *= k; z *= k; dr *= k; }
    else if (r2 < fr2) { const k = fr2 / r2; x *= k; y *= k; z *= k; dr *= k; }
    x = sc * x + p[0]; y = sc * y + p[1]; z = sc * z + p[2];
    dr = dr * Math.abs(sc) + 1;
  }
  return Math.sqrt(x * x + y * y + z * z) / Math.abs(dr);
}

export function deApollonian(p, prm) {
  let x = p[0], y = p[1], z = p[2];
  [y, z] = rot2(y, z, prm.angle);
  let scale = 1;
  for (let i = 0; i < ITER.apollonian; i++) {
    x = -1 + 2 * (0.5 * x + 0.5 - Math.floor(0.5 * x + 0.5));
    y = -1 + 2 * (0.5 * y + 0.5 - Math.floor(0.5 * y + 0.5));
    z = -1 + 2 * (0.5 * z + 0.5 - Math.floor(0.5 * z + 0.5));
    const r2 = x * x + y * y + z * z;
    const k = prm.scale / r2;
    x *= k; y *= k; z *= k; scale *= k;
  }
  return 0.25 * Math.abs(y) / scale;
}

export const DE = { menger: deMenger, kifs: deKifs, mandelbox: deMandelbox, apollonian: deApollonian };

// =============================================================================
// LES SHADERS
// =============================================================================
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const DE_GLSL = {
  menger: `
  float map(vec3 p, out float trap) {
    vec3 q = mod(p + 1.0, 2.0) - 1.0;
    q.yz = rot(uAngle) * q.yz;
    float d = -1e5, s = 1.0;
    trap = 1e5;
    for (int m = 0; m < ${ITER.menger}; m++) {
      vec3 a = mod(q * s, 2.0) - 1.0;
      s *= uScale;
      vec3 r = abs(uFold - uScale * abs(a));
      float c = (min(min(max(r.x, r.y), max(r.y, r.z)), max(r.z, r.x)) - 1.0) / s;
      d = max(d, c);
      trap = min(trap, dot(a, a) * 0.5 + float(m) * 0.13);
    }
    return d;
  }`,
  kifs: (iter) => `
  float map(vec3 p, out float trap) {
    vec3 z = mod(p + 2.0, 4.0) - 2.0;
    trap = 1e5;
    for (int i = 0; i < ${iter}; i++) {
      z = abs(z);
      if (z.x < z.y) z.xy = z.yx;
      if (z.x < z.z) z.xz = z.zx;
      if (z.y < z.z) z.yz = z.zy;
      z.xy = rot(uAngle) * z.xy;
      z = uScale * z - uOffset * (uScale - 1.0);
      trap = min(trap, length(z) * 0.25);
    }
    return (length(z) - 2.0) * pow(uScale, -${iter}.0);
  }`,
  mandelbox: `
  float map(vec3 p, out float trap) {
    p.yz = rot(uAngle) * p.yz;
    vec3 z = p;
    float dr = 1.0;
    trap = 1e5;
    for (int i = 0; i < ${ITER.mandelbox}; i++) {
      z = clamp(z, -uFold, uFold) * 2.0 - z;
      float r2 = dot(z, z);
      if (r2 < uMinR2) { float k = uFixR2 / uMinR2; z *= k; dr *= k; }
      else if (r2 < uFixR2) { float k = uFixR2 / r2; z *= k; dr *= k; }
      z = uScale * z + p;
      dr = dr * abs(uScale) + 1.0;
      trap = min(trap, r2);
    }
    return length(z) / abs(dr);
  }`,
  apollonian: `
  float map(vec3 p, out float trap) {
    p.yz = rot(uAngle) * p.yz;
    float scale = 1.0;
    trap = 1e5;
    for (int i = 0; i < ${ITER.apollonian}; i++) {
      p = -1.0 + 2.0 * fract(0.5 * p + 0.5);
      float r2 = dot(p, p);
      float k = uScale / r2;
      p *= k; scale *= k;
      trap = min(trap, r2 * 0.6 + float(i) * 0.1);
    }
    return 0.25 * abs(p.y) / scale;
  }`
};

const EPS_MUL = { menger: 1, kifs: 1, mandelbox: 3, apollonian: 1.5 };   // le détail intrinsèque de chaque DE

function buildFragmentShader(kind, L, tier) {
  const eps = L.eps * EPS_MUL[kind];
  const kifs = TIER_KIFS[tier];
  const steps = kind === 'kifs' && kifs ? kifs.steps : L.steps;
  const de = kind === 'kifs' ? DE_GLSL.kifs(kifs ? kifs.iter : ITER.kifs) : DE_GLSL[kind];
  return `
  precision highp float;
  uniform vec3 uCamPos;
  uniform vec3 uCamRight;
  uniform vec3 uCamUp;
  uniform vec3 uCamFwd;
  uniform float uTanHalfFov;
  uniform float uAspect;
  uniform float uScale;
  uniform float uFold;
  uniform float uMinR2;
  uniform float uFixR2;
  uniform vec3 uOffset;
  uniform float uAngle;
  uniform float uFrame;
  uniform vec3 uPal[5];
  uniform float uPalShift;
  uniform float uGlow;
  uniform vec3 uGlowColor;
  uniform vec3 uFog;
  uniform vec3 uBack;
  uniform vec3 uLightOff;
  uniform float uFlash;
  uniform float uStepsView;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;

  mat2 rot(float a) { float c = cos(a), s = sin(a); return mat2(c, -s, s, c); }
  float hash(vec3 p) { p = fract(p * 0.1031); p += dot(p, p.yzx + 33.33); return fract((p.x + p.y) * p.z); }
  ${de}

  // un dégradé de cinq couleurs, cyclique
  vec3 palette(float t) {
    t = fract(t) * 5.0;
    float i = floor(t), f = t - i;
    vec3 a = i < 0.5 ? uPal[0] : i < 1.5 ? uPal[1] : i < 2.5 ? uPal[2] : i < 3.5 ? uPal[3] : uPal[4];
    vec3 b = i < 0.5 ? uPal[1] : i < 1.5 ? uPal[2] : i < 2.5 ? uPal[3] : i < 3.5 ? uPal[4] : uPal[0];
    return mix(a, b, smoothstep(0.0, 1.0, f));
  }
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
${L.normals ? `
  vec3 calcNormal(vec3 p, float t) {
    float e = max(0.0004, 0.0008 * t), tr;
    vec2 k = vec2(1.0, -1.0);
    return normalize(k.xyy * map(p + k.xyy * e, tr) + k.yyx * map(p + k.yyx * e, tr)
                   + k.yxy * map(p + k.yxy * e, tr) + k.xxx * map(p + k.xxx * e, tr));
  }` : ''}

  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec3 rd = normalize(uCamFwd + uCamRight * ndc.x * uAspect * uTanHalfFov + uCamUp * ndc.y * uTanHalfFov);
    vec3 ro = uCamPos;
    float trap, trapHit = 0.0;
    float d0 = map(ro, trap);
    // jitter temporel du point de départ : les couches se brouillent d'une frame à l'autre
    float t = d0 * 0.2 * hash(vec3(gl_FragCoord.xy, uFrame));
    float glow = 0.0;
    float steps = 0.0;
    bool hit = false;
    for (int i = 0; i < ${steps}; i++) {
      vec3 p = ro + rd * t;
      float d = map(p, trap);
      steps += 1.0;
      ${L.glow ? 'glow += 1.0 / (1.0 + pow(d / (0.03 * t + 0.003), 2.0));' : ''}
      if (d < ${eps.toFixed(4)} * t) { hit = true; trapHit = trap; break; }
      t += d;
      if (t > 40.0) break;
    }
    float ao = 1.0 - steps / ${steps.toFixed(1)};
    ao = ao * ao * ao;
    if (uStepsView > 0.5) { gl_FragColor = vec4(vec3(steps / ${steps.toFixed(1)}), 1.0); return; }
    vec3 col;
    if (hit) {
      vec3 p = ro + rd * t;
      // la couleur suit la structure (l'orbite), pas la position
      vec3 base = palette(log(1.0 + trapHit * 3.0) * 0.45 + uPalShift);
      float diff = 1.0, rim = 0.0;
${L.normals ? `
      vec3 n = calcNormal(p, t);
      vec3 lp = uCamPos + uLightOff;
      vec3 l = normalize(lp - p);
      diff = 0.15 + 0.85 * max(dot(n, l), 0.0);
      rim = pow(1.0 - max(dot(n, -rd), 0.0), 3.0);` : ''}
      col = base * diff * (0.12 + 0.88 * ao) + uBack * rim * 0.4 * ao;
      col = mix(col, uFog, 1.0 - exp(-t * 0.09));
    } else {
      col = uFog;
    }
    ${L.glow ? 'col += uGlowColor * min(glow, 6.0) * uGlow * 0.03;' : ''}
    col = mix(col * 0.85, vec3(1.0), uFlash);
    gl_FragColor = vec4(col * safeAtt(vUv), 1.0);
  }
  `;
}

const upscaleFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;

// Un matériau par (fractale, tier), jamais disposé : voir l'en-tête.
const MATERIALS = new Map();

function makeUniforms() {
  return {
    uCamPos: { value: new THREE.Vector3() },
    uCamRight: { value: new THREE.Vector3(1, 0, 0) },
    uCamUp: { value: new THREE.Vector3(0, 1, 0) },
    uCamFwd: { value: new THREE.Vector3(0, 0, -1) },
    uTanHalfFov: { value: Math.tan(THREE.MathUtils.degToRad(FOV_MIN / 2)) },
    uAspect: { value: 1.78 },
    uScale: { value: 3.0 }, uFold: { value: 1.0 }, uMinR2: { value: 0.25 }, uFixR2: { value: 1.0 },
    uOffset: { value: new THREE.Vector3(1, 1, 1) }, uAngle: { value: 0 },
    uFrame: { value: 0 },
    uPal: { value: [0, 1, 2, 3, 4].map(() => new THREE.Color(0.5, 0.5, 0.5)) },
    uPalShift: { value: 0 },
    uGlow: { value: 0.5 }, uGlowColor: { value: new THREE.Color(1, 1, 1) },
    uFog: { value: new THREE.Color(0.02, 0.02, 0.05) }, uBack: { value: new THREE.Color(0.8, 0.3, 0.9) },
    uLightOff: { value: new THREE.Vector3(0.2, 0.3, 0) },
    uFlash: { value: 0 }, uStepsView: { value: 0 },
    uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
    uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
  };
}

export class FractalVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const k = urlParam('fractalkind');
    this._forceKind = k && KINDS.includes(k) ? k : null;
    this._stepsView = urlParam('fractalsteps') === '1';
    this._freeze = urlParam('fractalfreeze') === '1';
    this._postProcessing = { bloomStrength: 0.4, bloomThreshold: 0.78 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    const L = this._layout;
    this._time = 0; this._frame = 0; this._paused = false;
    this._uniforms = makeUniforms();
    this._uniforms.uStepsView.value = this._stepsView ? 1 : 0;
    this._buffer = new FeedbackBuffer({ scale: L.scale, single: true });
    this._volScene = new THREE.Scene();
    this._volQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), null);
    this._volQuad.frustumCulled = false;
    this._volScene.add(this._volQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._upscaleUniforms = { uTex: { value: null } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._upscaleUniforms, vertexShader: quadVertexShader, fragmentShader: upscaleFragmentShader,
      depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false;
    this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // l'état musical
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._free = 0; this._beats = 0; this._phrases = 0; this._secondsBeat = 0.5;
    this._pulseAge = 10; this._pulseAmp = 0; this._pulseGain = 1;
    this._low = 0.5; this._mid = 0.4; this._high = 0.4;
    this._angle = 0; this._palShift = 0;
    this._scaleMul = 1; this._scaleFrom = 1; this._scaleTo = 1; this._scaleT = 1;
    this._flashUntil = -1; this._dropUntil = -1;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._modeSeen = undefined;
    this._paletteSeen = undefined;
    this._kindIndex = 0;
    this._seed = 11;
    this._log = [];

    // la caméra
    this._pos = new THREE.Vector3(0, 0, 0);
    this._fwd = new THREE.Vector3(1, 0, 0);
    this._roll = 0;
    this._minDe = Infinity;
    this._setKind(this._forceKind || L.kinds[0], true);
  }

  _random() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  _material(kind) {
    const L = this._layout;
    const key = `${kind}:${this._tier}:${L.steps}:${L.glow ? 1 : 0}:${L.normals ? 1 : 0}`;
    let mat = MATERIALS.get(key);
    if (!mat) {
      mat = new THREE.ShaderMaterial({
        uniforms: makeUniforms(), vertexShader: quadVertexShader, fragmentShader: buildFragmentShader(kind, L, this._tier),
        depthTest: false, depthWrite: false
      });
      MATERIALS.set(key, mat);
    }
    return mat;
  }

  // Changer de fractale : un nouveau matériau (en cache), des paramètres de
  // base, et une position de départ libre.
  _setKind(kind, first) {
    this._kind = kind;
    this._base = BASE[kind];
    this._prm = { scale: this._base.scale, fold: this._base.fold, minR2: this._base.minR2, fixR2: this._base.fixR2, offset: this._base.offset.slice(), angle: this._base.angle || 0,
      iter: kind === 'kifs' && TIER_KIFS[this._tier] ? TIER_KIFS[this._tier].iter : undefined };
    this._angle = this._base.angle || 0;
    this._scaleMul = 1; this._scaleFrom = 1; this._scaleTo = 1; this._scaleT = 1;
    this._volQuad.material = this._material(kind);
    // les uniformes de ce matériau sont partagés par toutes les instances :
    // on y recopie l'état de celle-ci
    const u = this._volQuad.material.uniforms;
    for (const k in this._uniforms) if (u[k] && k !== 'uSafeAspect' && k !== 'uSafeCenter' && k !== 'uSafeHalfSize' && k !== 'uSafeFeather') u[k].value = this._uniforms[k].value;
    this._uniforms = u;
    // un point de départ libre : le couloir pour Menger, sinon le point le
    // plus dégagé parmi quelques tirages
    if (kind === 'menger') { this._pos.set(0.3, 0, 0); this._fwd.set(1, 0, 0); }
    else {
      let best = null, bestDe = -1;
      for (let i = 0; i < 48; i++) {
        const r = kind === 'apollonian' ? 1 : 2;
        let c = [this._random() * 2 * r - r, this._random() * 2 * r - r, this._random() * 2 * r - r];
        if (kind === 'mandelbox') {
          // la coquille entre le coeur encombré et le vide : les grandes salles
          const l = Math.hypot(c[0], c[1], c[2]) || 1, rr = 2.2 + this._random() * 2.5;
          c = [c[0] / l * rr, c[1] / l * rr, c[2] / l * rr];
        }
        const d = this._de(c);
        // un point dégagé mais dans la structure : ni collé au mur, ni dans le vide
        const score = d > 0.12 && d < FREE_DE ? d + 1 : d;
        if (score > bestDe) { bestDe = score; best = c; }
      }
      this._pos.set(best[0], best[1], best[2]);
      const a = this._random() * Math.PI * 2, b = (this._random() - 0.5) * 1.2;
      this._fwd.set(Math.cos(a) * Math.cos(b), Math.sin(b), Math.sin(a) * Math.cos(b)).normalize();
    }
    if (!first) this._log.push({ at: this._time, kind });
  }

  _de(p) { return DE[this._kind](p, this._prm); }

  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    if (pal === this._paletteSeen) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff4a8d, 0x3dd6ff, 0xffd13d, 0x9b5cff, 0x3dff9a];
    const hsl = { h: 0, s: 0, l: 0 };
    for (let i = 0; i < 5; i++) {
      const c = new THREE.Color(v[i % v.length]);
      c.getHSL(hsl);
      // cinq teintes en dégradé, saturation 0,85 : les intermédiaires entre deux vives
      const c2 = new THREE.Color(v[(i + 1) % v.length]); const h2 = { h: 0, s: 0, l: 0 }; c2.getHSL(h2);
      const h = i % 2 === 0 ? hsl.h : (hsl.h + h2.h) / 2;
      this._uniforms.uPal.value[i].setHSL(h, 0.85, 0.36);
    }
    const fog = new THREE.Color(v[v.length - 1]); fog.getHSL(hsl);
    this._uniforms.uFog.value.setHSL(hsl.h, 0.6, 0.035);
    const back = new THREE.Color(v[1 % v.length]); back.getHSL(hsl);
    this._uniforms.uBack.value.setHSL(hsl.h, 0.9, 0.6);
    this._uniforms.uGlowColor.value.setHSL(hsl.h, 0.7, 0.7);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  // --- la caméra -----------------------------------------------------------------------
  _stepCamera(dt, speed) {
    const p = this._pos, f = this._fwd;
    const pa = [p.x, p.y, p.z];
    const de = this._de(pa);
    this._minDe = Math.min(this._minDe, de);
    // le repère : un « haut » stable, puis le roulis
    const up = Math.abs(f.y) > 0.9 ? _tmpA.set(1, 0, 0) : _tmpA.set(0, 1, 0);
    const right = _tmpB.crossVectors(f, up).normalize();
    const upc = _tmpC.crossVectors(right, f).normalize();
    let desired = _tmpD.copy(f);
    if (this._kind === 'menger') {
      // le couloir : l'axe x, ramené au centre (0 mod 2) en y et z
      desired.set(1, 0, 0);
      const cy = Math.round(p.y / 2) * 2, cz = Math.round(p.z / 2) * 2;
      p.y += (cy - p.y) * (1 - Math.exp(-dt / 1.5));
      p.z += (cz - p.z) * (1 - Math.exp(-dt / 1.5));
    } else if (de > FREE_DE) {
      // loin de tout : redescendre le gradient de la DE, vers la structure
      const e = 0.01;
      const gx = this._de([pa[0] + e, pa[1], pa[2]]) - de, gy = this._de([pa[0], pa[1] + e, pa[2]]) - de, gz = this._de([pa[0], pa[1], pa[2] + e]) - de;
      desired.set(-gx, -gy, -gz);
      if (desired.lengthSq() < 1e-10) desired.copy(f); else desired.normalize();
    } else {
      // cinq sondes autour de l'axe. PAS la plus libre : à l'extérieur d'une
      // structure, « le plus libre » est le vide, et la caméra s'y perd. La
      // direction dont la DE est la plus proche d'une distance confortable :
      // au milieu d'un couloir, et vers les parois quand il n'y en a pas.
      let bestScore = -1e9; const best = _tmpE.copy(f);
      const dirs = [f, _d1.copy(f).addScaledVector(right, PROBE_TILT), _d2.copy(f).addScaledVector(right, -PROBE_TILT),
        _d3.copy(f).addScaledVector(upc, PROBE_TILT), _d4.copy(f).addScaledVector(upc, -PROBE_TILT)];
      for (const d of dirs) {
        d.normalize();
        const q = [pa[0] + d.x * PROBE_DIST, pa[1] + d.y * PROBE_DIST, pa[2] + d.z * PROBE_DIST];
        const dd = this._de(q);
        const score = -Math.abs(dd - TARGET_DE) + (d === f ? 0.02 : 0);   // l'axe garde un léger avantage
        if (score > bestScore) { bestScore = score; best.copy(d); }
      }
      desired.copy(best);
    }
    const k = 1 - Math.exp(-dt / STEER_TAU_S);
    f.lerp(desired, k).normalize();
    // L'AVANCE EST BORNÉE PAR LA DE : jamais plus de la moitié de la distance
    // au plus proche mur. Lipschitz : la nouvelle position reste libre.
    const slow = clamp01((de - MIN_DE) / 0.2);
    let step = speed * dt * (0.15 + 0.85 * slow);
    step = Math.min(step, Math.max(0, de) * 0.5);
    p.addScaledVector(f, step);
    // LES MURS BOUGENT (l'impulsion) : si la position se retrouve dans la
    // matière ou collée, remonter le gradient de la DE jusqu'à être libre
    for (let i = 0; i < 4; i++) {
      const q = [p.x, p.y, p.z];
      const dq = this._de(q);
      if (dq >= MIN_DE * 0.5) break;
      const e = 0.004;
      let gx = this._de([q[0] + e, q[1], q[2]]) - dq, gy = this._de([q[0], q[1] + e, q[2]]) - dq, gz = this._de([q[0], q[1], q[2] + e]) - dq;
      const gl = Math.hypot(gx, gy, gz) || 1;
      const push = (MIN_DE * 0.5 - dq) + 0.004;
      p.x += gx / gl * push; p.y += gy / gl * push; p.z += gz / gl * push;
    }
    // le roulis
    this._roll += dt * 2 * Math.PI / (ROLL_BEATS * this._secondsBeat);
    const cr = Math.cos(this._roll), sr = Math.sin(this._roll);
    const U = this._uniforms;
    U.uCamPos.value.copy(p);
    U.uCamFwd.value.copy(f);
    U.uCamRight.value.copy(right).multiplyScalar(cr).addScaledVector(upc, sr);
    U.uCamUp.value.copy(upc).multiplyScalar(cr).addScaledVector(right, -sr);
    U.uLightOff.value.copy(U.uCamRight.value).multiplyScalar(0.25).addScaledVector(U.uCamUp.value, 0.35);
    return de;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    this._frame = (this._frame + 1) % 1024;
    this._applyPalette(profile);
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bands) { this._low = clamp01(bands.low); this._mid = clamp01(bands.mid); this._high = clamp01(bands.high); }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 0.5;
    const barS = 4 * this._secondsBeat;

    // --- la grille ---
    let beat = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
    else { this._free += dt; if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; } }
    let bar = false;
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;
    // une phrase majeure : un changement de segment de l'analyse, sinon une phrase sur quatre
    let major = false;
    const mode = director ? director.mode : null;
    if (phrase) { this._phrases++; if (this._phrases % MAJOR_EVERY_PHRASES === 0) major = true; }
    if (mode !== this._modeSeen) { if (this._modeSeen !== undefined && beat) major = true; if (beat || this._modeSeen === undefined) this._modeSeen = mode; }

    // --- le drop : vitesse doublée une mesure, glow maximal, impulsion x3 ---
    if (this._tickDrops(profile)) this._dropUntil = this._time + barS;
    const inDrop = this._time < this._dropUntil;

    // --- TEMPS : l'impulsion de repliement ---
    if (beat) {
      this._pulseAge = 0;
      this._pulseAmp = lerp(PULSE_MIN, PULSE_MAX, this._low) * (inDrop ? 3 : 1);
    }
    this._pulseAge += dt;
    const a = this._pulseAge;
    const env = a < PULSE_ATTACK_S ? a / PULSE_ATTACK_S : Math.exp(-(a - PULSE_ATTACK_S) / PULSE_DECAY_S);
    const pulse = this._pulseAmp * env;

    // --- MESURE : un cran de rotation interne, la palette glisse ---
    if (bar) {
      this._angle += BAR_ANGLE_STEP * (0.5 + this._mid);
      this._palShift += 0.2;
    }
    // --- PHRASE : l'échelle change de 6 %, sur deux temps ---
    if (phrase && !major) {
      this._scaleFrom = this._scaleMul;
      this._scaleTo = this._scaleTo > 1 ? 1 - PHRASE_SCALE * 0.5 : 1 + PHRASE_SCALE;
      this._scaleT = 0;
    }
    if (this._scaleT < 1) {
      this._scaleT = Math.min(1, this._scaleT + dt / (PHRASE_LERP_BEATS * this._secondsBeat));
      const e = this._scaleT * this._scaleT * (3 - 2 * this._scaleT);
      this._scaleMul = lerp(this._scaleFrom, this._scaleTo, e);
    }
    // --- PHRASE MAJEURE : changement de fractale, caché par un flash ---
    if (major && !this._forceKind && this._layout.kinds.length > 1) {
      const kinds = this._layout.kinds;
      this._kindIndex = (kinds.indexOf(this._kind) + 1) % kinds.length;
      this._setKind(kinds[this._kindIndex], false);
      this._flashUntil = this._time + FLASH_S;
    }
    const flash = this._time < this._flashUntil ? (this._flashUntil - this._time) / FLASH_S : 0;

    // --- les paramètres du shader et de la DE ---
    const B = this._base, U = this._uniforms;
    const s = B.scale * this._scaleMul * (1 + pulse);
    this._prm.scale = s; this._prm.fold = B.fold * (1 + pulse * 0.5); this._prm.minR2 = B.minR2 * (1 + pulse); this._prm.fixR2 = B.fixR2;
    this._prm.offset = [B.offset[0] * (1 + pulse), B.offset[1] * (1 + pulse), B.offset[2] * (1 + pulse)];
    this._prm.angle = this._angle + pulse * 0.5;
    U.uScale.value = s; U.uFold.value = this._prm.fold; U.uMinR2.value = this._prm.minR2; U.uFixR2.value = this._prm.fixR2;
    U.uOffset.value.set(this._prm.offset[0], this._prm.offset[1], this._prm.offset[2]);
    U.uAngle.value = this._prm.angle;
    U.uPalShift.value = this._palShift * 0.2;
    U.uGlow.value = inDrop ? 1.0 : 0.25 + 0.75 * this._high;
    U.uFlash.value = flash * flash;
    U.uFrame.value = this._frame;
    U.uTanHalfFov.value = Math.tan(THREE.MathUtils.degToRad(lerp(FOV_MIN, FOV_MAX, this._low) / 2));
    U.uAspect.value = safeZoneUniforms.uAspect.value || 1.78;

    // --- la caméra : une cellule par mesure, doublée sur un drop ---
    const speed = (B.cell / barS) * (inDrop ? 2 : 1);
    this._stepCamera(dt, this._freeze ? 0 : speed);
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    this._buffer.ensureSize(renderer);
    renderer.setRenderTarget(this._buffer.read);
    renderer.render(this._volScene, this._ortho);
    renderer.setRenderTarget(prev);
    this._upscaleUniforms.uTex.value = this._buffer.read.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._buffer) this._buffer.dispose();
    if (this._volQuad) this._volQuad.geometry.dispose();
    // les matériaux de raymarching restent en cache, volontairement
  }
}

const _tmpA = new THREE.Vector3(), _tmpB = new THREE.Vector3(), _tmpC = new THREE.Vector3(), _tmpD = new THREE.Vector3(), _tmpE = new THREE.Vector3();
const _d1 = new THREE.Vector3(), _d2 = new THREE.Vector3(), _d3 = new THREE.Vector3(), _d4 = new THREE.Vector3();
